import React, { useState } from "react";
import {
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { Button, Card, Col, Row } from "react-bootstrap";
import WalletIcon from "../../assets/images/purse.png";
import DepositIcon from "../../assets/images/deposit.png";
import WithdrawIcon from "../../assets/images/cash-withdrawal.png";
import TransferIcon from "../../assets/images/transfer.png";
import PrintIcon from "../../assets/images/printer.png";

import CustomTable from "../../components/Common/CustomTable";
import useApi from "../../Hooks/useApi";
import { getAccountDetails, getAccountTransactions } from "../../helpers/API";
import { useNavigate, useParams } from "react-router-dom";
import TransactionModal, {
  TRANSACTION_TYPES,
} from "../../components/TransactionModal";
import useFilter from "../../Hooks/useFilter";
import callApi from "../../helpers/CallApi";
import { exportToCSV, exportToPDF } from "../../helpers/dataExport";
import TransactionDetailModal from "./TransactionDetailModal";
import { formatINR } from "../../helpers";
import { DATE_FORMAT } from "../../helpers/dateFormat";

const Index = ({ t }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useFilter({
    sortField: "transactionDate",
  });
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [transactionType, setTransactionType] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const openTransactionModal = (type, account = null) => {
    setTransactionType(type);
    setSelectedAccount(account);
    setShowTransactionModal(true);
  };

  const handleTransactionComplete = () => {
    setRefresh((prev) => !prev);
  };
  const columns = [
    {
      name: t("account.statement.columns.transactionType"),
      selector: (row) => row.transactionType,
      grow: 2,
    },
    {
      name: t("account.statement.columns.amount"),
      selector: (row) => (
        <>
          {formatINR(row.amount)}
          {row?.isDebit ? (
            <span className="text-danger"> (Debit)</span>
          ) : (
            <span className="text-success"> (Credit)</span>
          )}
        </>
      ),
    },
    {
      name: t("account.statement.columns.balance"),
      selector: (row) => formatINR(row.balanceBeforeTransaction),
    },
    {
      name: t("account.statement.columns.transactionDate"),
      dateField: "transactionDate",
      sortable: true,
      sortField: "transactionDate",
      dateFormat: DATE_FORMAT.DISPLAY,
    },
    {
      name: t("account.statement.columns.remarks"),
      selector: (row) => row.description,
    },
  ];
  const [accountDetails, setAccountDetails] = useState({});
  useApi({
    callback: getAccountDetails,
    payload: id,
    dependencies: [id, refresh],
    onSuccess: (data) => {
      setAccountDetails({ ...data, "branch|ifsc": data.branch + " | " + data.ifsc });
    },
    onError: () => {
      if (id === "cash-account") {
        setAccountDetails({ accountType: "cash_account", balance: 0 });
      }
    },
  });
  const [accountTransactions] = useApi({
    callback: getAccountTransactions,
    payload: [id, filters],
    dependencies: [id, refresh, filters],
    onError: () => { },
  });

  const handleExport = (type) => {
    const columns = [
      {
        key: "transactionType",
        header: t("account.statement.columns.transactionType"),
      },
      {
        key: "amount",
        selector: (row) => formatINR(row.amount),
        header: t("account.statement.columns.amount"),
      },
      {
        key: "balanceBeforeTransaction",
        selector: (row) => formatINR(row.balanceBeforeTransaction),
        header: t("account.statement.columns.balance"),
      },
      {
        key: "transactionDate",
        header: t("account.statement.columns.transactionDate"),
      },
      {
        key: "description",
        header: t("account.statement.columns.remarks"),
      },
    ];
    callApi({
      callback: getAccountTransactions,
      payload: [id, { pagination: false }],
      onSuccess: ({ data }) => {
        if (type === "csv") {
          exportToCSV({
            data,
            columns,
            filename: "account-statement.csv",
            addBOM: true,
          });
        } else {
          exportToPDF({
            data,
            columns,
            filename: "account-statement.pdf",
            title: "Account Statement",
            orientation: "l",
          });
        }
      },
    });
  };

  const ACTIONS = [
    {
      icon: DepositIcon,
      // isDisabled: true,
      //   onClick: handleDeposit,
      onClick: () =>
        openTransactionModal(TRANSACTION_TYPES.DEPOSIT, accountDetails),
      tooltip: t("account.statement.actionsTooltip.depositAmount"),
    },
    {
      icon: TransferIcon,
      isDisabled:
        !accountDetails?.balance || Number(accountDetails?.balance) <= 0,
      onClick: () =>
        openTransactionModal(TRANSACTION_TYPES.TRANSFER, accountDetails),
      tooltip: t("account.statement.actionsTooltip.transferAmount"),
    },
    {
      icon: WithdrawIcon,
      isDisabled:
        !accountDetails?.balance || Number(accountDetails?.balance) <= 0,
      onClick: () =>
        openTransactionModal(TRANSACTION_TYPES.WITHDRAW, accountDetails),
      tooltip: t("account.statement.actionsTooltip.withdrawAmount"),
    },
    {
      icon: PrintIcon,
      //   color: "info",
      isDisabled: !accountTransactions?.data?.length,
      tooltip: t("account.statement.actionsTooltip.printStatement"),
      menu: [
        {
          label: t("account.common.exportPdf"),
          onClick: () => handleExport("pdf"),
        },
        {
          label: t("account.common.exportCsv"),
          onClick: () => handleExport("csv"),
        },
      ],
    },
  ];
  const accountFields = [
    [
      {
        label: t("account.statement.accountFields.accountNo"),
        key: "accountNumber",
      },
      {
        label: t("account.statement.accountFields.accountHolderName"),
        key: "accountHolderName",
      },
    ],
    ...(id === "cash-account"
      ? []
      : [
        [
          {
            label: t("account.statement.accountFields.bankName"),
            key: "bankName",
          },
          {
            label: t("account.statement.accountFields.branch") + " | " + t("account.statement.accountFields.ifsc"),
            key: "branch|ifsc",
          },
        ],
      ]),
  ];
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            navigation={true}
            path={"/record-book"}
            title={t("breadcrumbs.capitalInvestment.list.title")}
            breadcrumbItem={t(
              "breadcrumbs.capitalInvestment.list.breadcrumbItem"
            )}
          />
          <Card>
            <Card.Body>
              <Row>
                <Col lg={4}>
                  <div className="d-flex gap-3">
                    <img
                      src={WalletIcon}
                      alt="wallet-icon"
                      style={{ width: "100px", height: "100px" }}
                    />
                    <div>
                      <span className="fs-3 fw-bold">Balance</span>
                      <span className="fs-3 fw-medium d-block">
                        {formatINR(accountDetails?.balance || 0)}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col lg={8} className="d-flex gap-3">
                  {accountFields.map((column, columnIndex) => (
                    <div key={columnIndex} className="flex-fill">
                      <ul className="list-group list-group-flush">
                        {column.map(({ label, key }) => (
                          <li
                            key={key}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <span className="fw-bold">{label}</span>
                            <span
                              className="text-truncate"
                              style={{ maxWidth: "60%" }}
                            >
                              {accountDetails?.[key]}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Col>

              </Row>
            </Card.Body>
          </Card>

          <CustomTable
            tableTitle={t("account.statement.tableTitle")}
            showAddButton={false}
            headerContent={
              <Row className="align-items-center gy-2 gx-md-3">
                <Col xs={12} md={6}>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {ACTIONS.map(
                      (
                        { icon, onClick, menu, tooltip, isDisabled },
                        index
                      ) => (
                        <React.Fragment key={index}>
                          {menu ? (
                            <UncontrolledDropdown>
                              <DropdownToggle
                                id={`header-action-btn-${index}`}
                                tag="button"
                                className="btn btn-primary d-flex align-items-center justify-content-center"
                                disabled={isDisabled}
                                style={{ minWidth: "45px", minHeight: "38px" }}
                              >
                                <img
                                  src={icon}
                                  alt={tooltip}
                                  width={20}
                                  height={20}
                                  style={{
                                    objectFit: "contain",
                                    filter: "brightness(0) invert(1)",
                                  }}
                                />
                              </DropdownToggle>

                              <DropdownMenu end>
                                {menu.map((mi, i) => (
                                  <DropdownItem key={i} onClick={mi.onClick}>
                                    {mi.label}
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          ) : (
                            <Button
                              id={`header-action-btn-${index}`}
                              disabled={isDisabled}
                              onClick={onClick}
                              variant="primary"
                              className="d-flex align-items-center justify-content-center"
                              style={{ minWidth: "45px", minHeight: "38px" }}
                            >
                              <img
                                src={icon}
                                alt={tooltip}
                                width={20}
                                height={20}
                                style={{
                                  objectFit: "contain",
                                  filter: "brightness(0) invert(1)",
                                }}
                              />
                            </Button>
                          )}

                          <UncontrolledTooltip
                            target={`header-action-btn-${index}`}
                            placement="top"
                          >
                            {tooltip}
                          </UncontrolledTooltip>
                        </React.Fragment>
                      )
                    )}
                  </div>
                </Col>
                <Col
                  xs={12}
                  md={6}
                  className="d-flex justify-content-md-end justify-content-start"
                >
                  <Button
                    variant="primary"
                    className="w-md-auto"
                    onClick={() =>
                      navigate(
                        id === "cash-account"
                          ? "/record-book"
                          : "/account/cash-account"
                      )
                    }
                  >
                    {id === "cash-account"
                      ? t("account.list.buttons.goToBankAccount")
                      : t("account.list.buttons.goToCashAccount")}
                  </Button>
                </Col>
              </Row>
            }
            onStatusChange={(data) => setFilters(data)}
            showSearch={false}
            columns={columns}
            data={accountTransactions || []}
            onRowClicked={(row) => {
              setSelectedTransaction(row);
              setShowDetailModal(true);
            }}
          />
          <TransactionDetailModal
            show={showDetailModal}
            toggle={() => {
              setShowDetailModal(false);
              setSelectedTransaction(null);
            }}
            transaction={selectedTransaction}
          />
          <TransactionModal
            show={showTransactionModal}
            toggle={() => setShowTransactionModal(false)}
            transactionType={transactionType}
            selectedAccount={selectedAccount}
            onTransactionComplete={handleTransactionComplete}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Index);
